import { ComplianceAudioBadge } from "@/contexts/ComplianceAudioBadge";
import { ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/shared/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/ui/alert-dialog";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL, CARD_WALL_CAP, CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import { digitalHumanService, type VoiceProfileDto } from "../api/digitalHumanService";
import { voiceProfileSupportsTuning } from "@/entities/dh-profile/api/dhProfileService";
import { LegacyVoiceBadge } from "@/entities/dh-profile/ui/LegacyVoiceBadge";
import { DH_VOICE_RECORD_MAX_SECONDS, DH_VOICE_RECORD_MIN_SECONDS } from "../model/dhConsentText";
import { VOICE_USAGE_LABEL, presentUsages } from "../model/dhAssetTaxonomy";
import { VoiceRecordEnrollCard } from "./components/VoiceRecordEnrollCard";
import { DhVoiceGoLineMenu } from "./components/DhVoiceGoLineMenu";
import { errorText, errorToast } from "@/shared/lib/apiErrors";

const SOURCE_LABEL: Record<string, string> = { upload: "上传克隆", record: "即录即证" };

/**
 * 声音库(B5 二刀,终稿屏⑦③):声纹授权红线条 + 克隆新声音(即录即证卡 / 上传样本卡)+
 * 我的声音(试听)+ 公共声音分区(内置音色,与出片页 DH_VOICE_OPTIONS 单源;预置音色无
 * 样音 → 不做假试听)。治理红线:录音克隆须本人、入库后租户内共用(api VoiceProfileServiceImpl 已撤人际隔离)、跨公司隔离,voice_id 不下发前端。
 *
 * <b>只作为「我的资产」Tab 的一块内容渲染,自身不出页头</b>(页头由 `DhRealAssetsPanel` 的外壳给)。
 * ★2026-08-09 删掉了 `embedded` prop 与它守着的那段 PageHeader,理由同 `AvatarProfilePage`:
 * 全仓只有 `DhRealAssetsPanel` 挂载本页且恒传 `embedded`,那段页头运行时不可达。
 * 顺带没了的还有页头上那个泛入口「去出片」—— 它本来就是重复的:按音色分流的真入口在**每张音色卡上**
 * (`onGo`,一个音色可去三条线),而页头那个只能去真人口播一条,不可达之外还更弱。
 */
export default function VoiceProfilePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["dh", "voice-profiles"],
    queryFn: () => digitalHumanService.listVoiceProfiles(),
  });
  const view = listQuery.data;
  const profiles = view?.profiles ?? [];
  const used = view?.used ?? 0;
  const limit = view?.limit ?? null;
  const atLimit = limit != null && used >= limit;

  // C2 公共音色:平台策展只读资产(带试听样音 presign;拉取失败静默隐藏该区)。
  const presetVoicesQuery = useQuery({
    queryKey: ["dh", "preset-voices"],
    queryFn: () => digitalHumanService.listPresetVoices(),
    staleTime: 60_000,
    retry: false,
  });
  const presetVoices = presetVoicesQuery.data ?? [];
  const [presetUsage, setPresetUsage] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["dh", "voice-profiles"] });

  const onDelete = async (p: VoiceProfileDto) => {
    try {
      await digitalHumanService.deleteVoiceProfile(p.id);
      toast({ title: "已删除声纹", description: `「${p.label}」已下架并释放。` });
      await refresh();
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "删除失败" }));
    }
  };

  return (
    <div className="space-y-6">
      {/* 声纹授权红线条(终稿屏⑦③) */}
      <div
        className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground"
        data-testid="dh-voice-redline"
      >
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <span>
          <b className="font-medium text-foreground">声纹授权</b> ——
          录音克隆须本人(现场克隆=朗读引导文本即录即证);授权入库后全公司共用出片,跨公司不可用。
          语速在出片时调,不改音色资产本身。
        </span>
      </div>

      {/* 克隆新声音:即录即证(主推)+ 上传样本 */}
      <section className="space-y-3">
        <h2 className="flex items-baseline gap-2 text-sm font-semibold">
          克隆新声音
          <span className="text-xs font-normal text-muted-foreground">
            租户已用 {used} / {limit ?? "∞"} · 我的 {profiles.length}
          </span>
        </h2>
        {atLimit && (
          <p className="text-xs text-warning">
            租户声纹配额已满(全租户共用),请联系管理员清理不用的声纹或升配。
          </p>
        )}
        {/* ★0915 归一批 A:删掉右侧「上传样本克隆」整张卡。
            它与左卡的「上传现成录音」旁路打的是**同一个端点、同一组字段**
            (`enrollVoiceProfile` → POST /voice-profiles,file/label/consentAcknowledged),
            后端 `"record".equals(sampleSource) ? "record" : "upload"` —— 右卡不传 source,
            判进的也是 upload。两张卡产出的声纹一模一样,不是两种能力。

            连带退役的还有那句「下面两种任选其一」(0813 琳琳问「这是二选一吗」时打的补丁):
            补丁存在本身就是这两张卡冗余的证据 —— 只剩一张卡就不用解释怎么选了。

            ★右卡独有的样本内容红线 `DH_VOICE_SAMPLE_RULE` 已搬进左卡的上传分支
            (见 VoiceRecordEnrollCard)—— 那条是 0813 实测教训(用户传了「数 1 到 10」,
            出片就把数数夹进了口播),不能随卡一起删掉。 */}
        <VoiceRecordEnrollCard disabled={atLimit} />
      </section>

      {/* 我的声音 */}
      <section className="space-y-3">
        <h2 className="flex items-baseline gap-2 text-sm font-semibold">
          我的声音
          <span className="text-xs font-normal text-muted-foreground">点卡即试听 · 已授权存证</span>
        </h2>
        <QueryErrorBanner
          isError={listQuery.isError}
          message={listQuery.error ? errorText(listQuery.error) : undefined}
          onRetry={() => listQuery.refetch()}
        />
        {listQuery.isLoading ? (
          // BP-07:加载态按最终声音卡形状的骨架屏(横条:文案行 + 播放器条)
          <div className={CARD_WALL}>
            <div className={CARD_WALL_CAP} data-testid="dh-voice-skeleton">
              {Array.from({ length: 3 }).map((_, i) => (
                <VoiceCardSkeleton key={i} />
              ))}
            </div>
          </div>
        ) : listQuery.isError ? null : profiles.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              还没有声纹。现场朗读引导文本({DH_VOICE_RECORD_MIN_SECONDS} 秒以上,录到{" "}
              {DH_VOICE_RECORD_MAX_SECONDS} 秒自动停),或上传一段本人语音,克隆专属音色。
            </CardContent>
          </Card>
        ) : (
          <div className={CARD_WALL}>
            <div className={CARD_WALL_CAP}>
              {profiles.map((p) => (
                <Card key={p.id} className={cn("shadow-sm", CARD_WALL_RADIUS)}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">{p.label}</span>
                        <Badge variant="secondary" className="text-2xs">
                          {SOURCE_LABEL[p.source] ?? "未知来源"}
                        </Badge>
                        <Badge variant="outline" className="text-2xs text-muted-foreground">
                          已授权
                        </Badge>
                        {/* 1006:旧版声纹(cosyvoice)方言/多语言/演绎不生效,重新录一次即可解锁 */}
                        {!voiceProfileSupportsTuning(p) && (
                          <span title="方言、多语言与演绎方式对这条声纹不生效 —— 重新录一次即可解锁">
                            <LegacyVoiceBadge />
                          </span>
                        )}
                      </div>
                      {p.sampleUrl && (
                        <audio
                          onPlay={pauseOtherAudio}
                          controls
                          src={p.sampleUrl}
                          className="mt-2 h-8 w-full max-w-xs"
                          aria-label={`试听 ${p.label}`}
                        >
                          {/* 声纹样本无字幕轨(a11y 规则占位)。 */}
                          <track kind="captions" />
                        </audio>
                      )}
                      {/* 声音 V2(2026-09-08):声纹卡也挂「用 TA 出片」分流菜单 ——
                          此前只有公共声音有直达,自己克隆完还得回工作台手动选一遍。
                          state 走声纹口径(voiceProfileId),落地页选中「我的声纹」。 */}
                      <div className="mt-1">
                        <DhVoiceGoLineMenu
                          voiceId={`profile-${p.id}`}
                          voiceLabel={p.label}
                          voiceProfileId={p.id}
                          onGo={(route, state) => navigate(route, { state })}
                        />
                      </div>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`删除 ${p.label}`}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>删除声纹「{p.label}」?</AlertDialogTitle>
                          <AlertDialogDescription>
                            删除后将释放该音色,已生成的视频不受影响,但无法再用它出新片。此操作不可撤销。
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>取消</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(p)}>
                            确认删除
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ★拿不到数据时**不能整区消失**(0810 走查 BUG-DH-04):页头副标明写「或从公共声音里
          试听挑一款」,而这里原来是 `presetVoices.length > 0 &&` —— 接口 403/超时就静默隐藏,
          用户看到的是一个自相矛盾的页面(说有、找不到),也没有任何线索能往下查。
          现在三态都出声:加载中 / 拿不到(可重试) / 真的没有。 */}
      {presetVoices.length === 0 && (
        <section className="space-y-2" data-testid="dh-public-voices-empty">
          <h2 className="text-sm font-semibold">公共声音</h2>
          {presetVoicesQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">正在加载公共声音…</p>
          ) : presetVoicesQuery.isError ? (
            <div className="flex items-center gap-3 rounded-lg border border-warning/40 bg-warning/5 p-3">
              <p className="text-xs text-muted-foreground">
                公共声音暂时加载不出来 —— 你自己克隆的声音不受影响,仍可正常出片。
              </p>
              <Button size="sm" variant="outline" onClick={() => presetVoicesQuery.refetch()}>
                重试
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              平台还没有上架公共声音,先用上面的「克隆新声音」录一个吧。
            </p>
          )}
        </section>
      )}

      {/* 公共声音(C2):平台策展 preset-voices 真数据 —— 有样音才渲染试听(不做假试听);
          用途筛词表单源在 dhAssetTaxonomy。 */}
      {presetVoices.length > 0 && (
        <section className="space-y-3" data-testid="dh-public-voices">
          <h2 className="flex items-baseline gap-2 text-sm font-semibold">
            公共声音
            <span className="text-xs font-normal text-muted-foreground">
              内置 {presetVoices.length} 款 · 开箱即用 · 不占克隆配额
            </span>
          </h2>
          {presentUsages(presetVoices).length > 0 && (
            <div
              className="flex w-fit flex-wrap items-center gap-1 rounded-lg border bg-muted/40 p-1"
              data-testid="dh-preset-usage-chips"
            >
              <button
                type="button"
                onClick={() => setPresetUsage(null)}
                aria-pressed={presetUsage === null}
                className={`rounded px-2 py-0.5 text-2xs transition-colors ${
                  presetUsage === null
                    ? "bg-background font-medium text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                全部
              </button>
              {presentUsages(presetVoices).map((u) => (
                <button
                  key={u.key}
                  type="button"
                  onClick={() => setPresetUsage(presetUsage === u.key ? null : u.key)}
                  aria-pressed={presetUsage === u.key}
                  className={`rounded px-2 py-0.5 text-2xs transition-colors ${
                    presetUsage === u.key
                      ? "bg-background font-medium text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {u.label}
                </button>
              ))}
            </div>
          )}
          <div className={CARD_WALL}>
            <div className={CARD_WALL_CAP}>
              {presetVoices
                .filter((v) => !presetUsage || v.usage === presetUsage)
                .map((v) => (
                  <div
                    key={v.id}
                    className={cn(
                      "flex flex-col gap-2 border p-3 transition hover:border-primary/50",
                      CARD_WALL_RADIUS
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium">{v.label}</span>
                      <Badge variant="secondary" className="shrink-0 text-2xs">
                        {v.usage ? (VOICE_USAGE_LABEL[v.usage] ?? "内置") : "内置"}
                      </Badge>
                    </div>
                    {/* 有样音才给试听:样音是该音色本人念同一句固定文案的真产物 */}
                    {v.sampleUrl && (
                      <audio
                        onPlay={pauseOtherAudio}
                        controls
                        // metadata 而非 none:none 时播放器时长恒显 0:00/0:00,读起来像空音频;
                        // metadata 只拉头部拿时长,不拉音频体。
                        preload="metadata"
                        src={v.sampleUrl}
                        className="h-8 w-full"
                        aria-label={`试听 ${v.label}`}
                      >
                        {/* 样音无字幕轨(a11y 规则占位)。 */}
                        <track kind="captions" />
                      </audio>
                    )}
                    {v.sampleUrl && <ComplianceAudioBadge className="self-start" />}
                    <DhVoiceGoLineMenu
                      voiceId={v.id}
                      voiceLabel={v.label}
                      usage={v.usage}
                      onGo={(route, state) => navigate(route, { state })}
                    />
                  </div>
                ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/** 声音卡骨架屏(BP-07):与声音卡同形状 —— 一行文案 + 一条播放器高度。 */
function VoiceCardSkeleton() {
  return (
    <div className={cn("border bg-card p-3", CARD_WALL_RADIUS)}>
      <div className="h-4 w-2/5 fx-skeleton rounded bg-muted" />
      <div className="mt-2 h-8 w-full fx-skeleton rounded bg-muted" />
    </div>
  );
}
